import React from 'react';
import { X } from 'lucide-react';

function UserGuideModal({ onClose }) {
    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="w-full max-w-5xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b shrink-0" style={{ borderColor: 'var(--border-color)' }}>
                    <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>AgenticScholar User Guide</h2>
                    <button 
                        onClick={onClose} 
                        className="p-2 rounded-md hover:bg-black/5 transition-colors" 
                        style={{ color: 'var(--text-secondary)' }}
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {/* Content - Scrollable */}
                <div className="overflow-y-auto p-8 space-y-8 text-left leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                    
                    {/* 1. Introduction */}
                    <section>
                        <h3 className="text-xl font-bold mb-4 pb-2 border-b" style={{ color: 'var(--primary-color)', borderColor: 'var(--border-color)' }}>1. Introduction</h3>
                        <p className="mb-4">
                            <strong>AgenticScholar</strong> is an intelligent academic data management system designed to assist researchers in organizing, analyzing, and reasoning over large-scale scholarly corpora. Beyond traditional reference management, AgenticScholar leverages agent-based AI to support open-ended and complex scholarly queries, such as research trend analysis and research idea exploration.
                        </p>
                    </section>

                    {/* 2. Getting Started */}
                    <section>
                        <h3 className="text-xl font-bold mb-4 pb-2 border-b" style={{ color: 'var(--primary-color)', borderColor: 'var(--border-color)' }}>2. Getting Started</h3>
                        
                        <h4 className="text-lg font-semibold mb-3">2.1 Create Your Account</h4>
                        <p className="mb-2">To begin using AgenticScholar, you must first create a user account.</p>
                        <ol className="list-decimal list-inside space-y-2 ml-2 mb-4">
                            <li>Click the <strong>Sign up</strong> button on the login page.</li>
                            <li>Complete the registration form:
                                <ul className="list-disc list-inside ml-6 mt-1 space-y-1 text-sm">
                                    <li><strong>Username</strong>: Must consist of lowercase letters only (e.g., adctest). Special characters and uppercase letters are not allowed.</li>
                                    <li><strong>Name</strong>: Enter your full name.</li>
                                    <li><strong>Email</strong>: Enter a valid email address.</li>
                                    <li><strong>Password</strong>: Create a secure password.</li>
                                </ul>
                            </li>
                            <li>Click <strong>Sign up</strong> to create your account and log in.</li>
                        </ol>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
                            <div className="flex flex-col items-center">
                                <img src="/images/1_Sec_21_step1.png" alt="Step 1: Sign Up Button" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                <p className="text-sm text-gray-500 mt-2 font-medium">Step 1: Click Sign Up</p>
                            </div>
                            <div className="flex flex-col items-center">
                                <img src="/images/2_Sec_21_step2.png" alt="Step 2: Registration Form" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                <p className="text-sm text-gray-500 mt-2 font-medium">Step 2: Fill Registration Form</p>
                            </div>
                        </div>

                        <h4 className="text-lg font-semibold mb-3 mt-8">2.2 Familiarize Yourself with the Dashboard</h4>
                        <p className="mb-2">After logging in, the main interface is divided into three primary areas:</p>
                        <ul className="list-disc list-inside space-y-2 ml-2 mb-4">
                            <li><strong>Left Sidebar</strong>: This is your control panel for Managing Knowledge Bases, with paper lists and taxonomy trees (e.g., Problems and Methods).</li>
                            <li><strong>Center Canvas</strong>: This is your main working area where you enter queries and view system responses.</li>
                            <li><strong>Right Panel</strong>: This area displays your chat history and the Execution Trace.</li>
                        </ul>
                        <div className="my-6">
                            <img src="/images/3_Sec22_step1.png" alt="Dashboard Layout" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                        </div>
                    </section>

                    {/* 3. Create a KB */}
                    <section>
                        <h3 className="text-xl font-bold mb-4 pb-2 border-b" style={{ color: 'var(--primary-color)', borderColor: 'var(--border-color)' }}>3. Create a Knowledge Base</h3>
                        <p className="mb-3">Before you can analyze papers, you must create at least one Knowledge Base to store them.</p>
                        <ol className="list-decimal list-inside space-y-2 ml-2">
                            <li>In the <strong>Left Sidebar</strong>, locate the <strong>Current Knowledge Base</strong> section, click the <strong>+ (Plus)</strong> button.</li>
                            <li>Enter a name for the new Knowledge Base (e.g., vector_search), and confirm to create the Knowledge Base.</li>
                        </ol>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
                            <div className="flex flex-col items-center">
                                <img src="/images/4_Sec3_step1.png" alt="Create KB Step 1" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                <p className="text-sm text-gray-500 mt-2 font-medium">Step 1: Click Plus Button</p>
                            </div>
                            <div className="flex flex-col items-center">
                                <img src="/images/5_Sec3_step2.png" alt="Create KB Step 2" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                <p className="text-sm text-gray-500 mt-2 font-medium">Step 2: Enter KB Name</p>
                            </div>
                        </div>
                    </section>

                    {/* 4. Upload Papers */}
                    <section>
                        <h3 className="text-xl font-bold mb-4 pb-2 border-b" style={{ color: 'var(--primary-color)', borderColor: 'var(--border-color)' }}>4. Upload Research Papers</h3>
                        <p className="mb-3">Once a Knowledge Base has been created, you can populate it with research papers.</p>
                        <ol className="list-decimal list-inside space-y-2 ml-2">
                            <li>In the <strong>Left Sidebar</strong>, select your target <strong>Knowledge Base</strong>.</li>
                            <li>Click the <strong>Upload Paper</strong> button at the bottom, and choose one or more PDF files from your computer to upload.</li>
                            <li>Wait for the system to process the uploaded files.</li>
                        </ol>
                        <div className="my-6">
                            <img src="/images/6_Sec4_step1.png" alt="Upload Paper Steps" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                        </div>
                    </section>

                    {/* 5. Build Taxonomy */}
                    <section>
                        <h3 className="text-xl font-bold mb-4 pb-2 border-b" style={{ color: 'var(--primary-color)', borderColor: 'var(--border-color)' }}>5. Build an Academic Taxonomy</h3>
                        <p className="mb-3">AgenticScholar can automatically organize your uploaded papers into a structured taxonomy.</p>
                        <ol className="list-decimal list-inside space-y-2 ml-2">
                            <li>In the <strong>Left Sidebar</strong>, click the <strong>Create Taxonomy</strong> button (bottom-left corner).</li>
                            <li>After the taxonomy construction is complete, select <strong>Problems</strong> or <strong>Methods</strong> to view the corresponding taxonomy tree.</li>
                            <li>Click any node in the taxonomy tree to read its description.</li>
                        </ol>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                            <div className="flex flex-col items-center">
                                <img src="/images/7_Sec5_step1.png" alt="Create Taxonomy" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                <p className="text-sm text-gray-500 mt-2 font-medium">Step 1: Click Create Taxonomy</p>
                            </div>
                            <div className="flex flex-col items-center">
                                <img src="/images/8_Sec5_step2.png" alt="View Taxonomy" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                <p className="text-sm text-gray-500 mt-2 font-medium">Step 2: View Problems/Methods</p>
                            </div>
                            <div className="flex flex-col items-center">
                                <img src="/images/9_Sec5_step3.png" alt="Taxonomy Tree" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                <p className="text-sm text-gray-500 mt-2 font-medium">Step 3: Click Node</p>
                            </div>
                             <div className="flex flex-col items-center">
                                <img src="/images/10_Sec5_step4.png" alt="Node Details" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                <p className="text-sm text-gray-500 mt-2 font-medium">Step 4: View Details</p>
                            </div>
                        </div>
                    </section>

                    {/* 6. Scholarly Queries */}
                    <section>
                        <h3 className="text-xl font-bold mb-4 pb-2 border-b" style={{ color: 'var(--primary-color)', borderColor: 'var(--border-color)' }}>6. Ask Scholarly Queries</h3>
                        <p className="mb-6">AgenticScholar supports multiple modes of scholarly exploration, from single-paper analysis to topic-level reasoning over sets of papers.</p>

                        <div className="space-y-8">
                            {/* 6.1 */}
                            <div className="bg-white/50 p-6 rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
                                <h4 className="text-lg font-bold mb-3">6.1 Single Paper Exploration</h4>
                                <p className="mb-2">Use this mode to analyze or query a <strong>single paper</strong>.</p>
                                <ol className="list-decimal list-inside space-y-1 mb-4">
                                    <li>Select the target paper from the paper list.</li>
                                    <li>Either click the <strong>"Single Paper Exploration"</strong> button under suggested queries, or directly type a query.</li>
                                </ol>
                                <div className="my-4">
                                    <img src="/images/11_Sec61_step1.png" alt="Single Paper Query" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                </div>
                            </div>

                            {/* 6.2 */}
                            <div className="bg-white/50 p-6 rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
                                <h4 className="text-lg font-bold mb-3">6.2 Set of Papers Exploration</h4>
                                <p className="mb-2">Use this mode to query a <strong>set of papers</strong>.</p>
                                <ol className="list-decimal list-inside space-y-1 mb-4">
                                    <li>Select all relevant papers you wish to analyze.</li>
                                    <li>Either click the <strong>"Set of Papers Exploration"</strong> query from suggestions, or type a query.</li>
                                </ol>
                                <div className="my-4">
                                    <img src="/images/12_Sec62_step1.png" alt="Set of Papers Query" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                </div>
                            </div>

                            {/* 6.3 */}
                            <div className="bg-white/50 p-6 rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
                                <h4 className="text-lg font-bold mb-3">6.3 Topic-Level Exploration</h4>
                                <p className="mb-4">Topic-level exploration leverages the constructed taxonomies of Problems and Methods.</p>
                                
                                <div className="space-y-10">
                                    {/* 6.3.1 Trend */}
                                    <div>
                                        <h5 className="font-semibold text-md mb-2 underline">6.3.1 Research Trend Analysis</h5>
                                        <p className="text-sm mb-2">Visualize how a research area evolves over time.</p>
                                        <div className="grid grid-cols-1 gap-4">
                                            <div>
                                                <img src="/images/13_Sec631_step1.png" alt="Trend Analysis Step 1" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                                <p className="text-sm text-gray-500 mt-1">Select Topics & Query</p>
                                            </div>
                                            <div>
                                                <img src="/images/14_Sec631_step2.png" alt="Trend Analysis Result" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                                <p className="text-sm text-gray-500 mt-1">View Trend Chart</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 6.3.2 Milestone */}
                                    <div>
                                        <h5 className="font-semibold text-md mb-2 underline">6.3.2 Identify Milestone Papers</h5>
                                        <p className="text-sm mb-2">Identify the most influential or representative papers for a given topic.</p>
                                        <div className="grid grid-cols-1 gap-4">
                                            <div>
                                                <img src="/images/15_Sec632_step1.png" alt="Milestone Step 1" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                                <p className="text-sm text-gray-500 mt-1">Select Query for Milestones</p>
                                            </div>
                                            <div>
                                                <img src="/images/16_Sec632_step2.png" alt="Milestone Result" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                                <p className="text-sm text-gray-500 mt-1">View Ranked List</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* 6.3.3 Ideas */}
                                    <div>
                                        <h5 className="font-semibold text-md mb-2 underline">6.3.3 Discover New Research Ideas</h5>
                                        <p className="text-sm mb-2">Uncover gaps in the literature and generate potential research directions.</p>
                                        <div className="grid grid-cols-1 gap-4">
                                            <div>
                                                <img src="/images/17_Sec633_step1.png" alt="Research Ideas Step 1" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                                <p className="text-sm text-gray-500 mt-1">Select Query for Ideas</p>
                                            </div>
                                            <div>
                                                <img src="/images/18_Sec633_step2.png" alt="Research Ideas Result" className="w-full h-auto rounded-lg shadow-md border border-gray-200" />
                                                <p className="text-sm text-gray-500 mt-1">View Research Proposal</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                </div>

                {/* Footer */}
                <div className="p-6 border-t flex justify-end shrink-0" style={{ borderColor: 'var(--border-color)' }}>
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 rounded-lg font-medium transition shadow-sm"
                        style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
                        onMouseOver={(e) => e.target.style.backgroundColor = 'var(--primary-dark)'}
                        onMouseOut={(e) => e.target.style.backgroundColor = 'var(--primary-color)'}
                    >
                        Close Guide
                    </button>
                </div>
            </div>
        </div>
    );
}

export default UserGuideModal;